import axios, { AxiosError } from "axios";

import { appConfig } from "@/config/app.config";
import { downloadBlob } from "@/lib/exporters";
import type { ApiErrorBody } from "@/types/api";
import type {
  AdminLoginRequest,
  AdminLoginResult,
  AdminMe,
  AdminSettings,
  AdminStats,
  AdminWaitlistResponse
} from "@/types/admin";

const adminApi = axios.create({
  baseURL: appConfig.apiBaseUrl,
  timeout: 30000,
  withCredentials: true
});

function toAdminError(error: unknown): Error {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiErrorBody>;
    return new Error(
      axiosError.response?.data?.error?.message ||
        axiosError.message ||
        "The admin request failed. Please try again."
    );
  }

  return error instanceof Error ? error : new Error("The admin request failed. Please try again.");
}

export async function getAdminMe(): Promise<AdminMe> {
  try {
    const response = await adminApi.get<AdminMe>("/admin/me");
    return response.data;
  } catch (error) {
    throw toAdminError(error);
  }
}

export async function loginAdmin(payload: AdminLoginRequest): Promise<AdminLoginResult> {
  try {
    const response = await adminApi.post<AdminLoginResult>("/admin/login", payload);
    return response.data;
  } catch (error) {
    throw toAdminError(error);
  }
}

export async function logoutAdmin(): Promise<void> {
  try {
    await adminApi.post("/admin/logout");
  } catch (error) {
    throw toAdminError(error);
  }
}

export async function getAdminStats(): Promise<AdminStats> {
  try {
    const response = await adminApi.get<AdminStats>("/admin/stats");
    return response.data;
  } catch (error) {
    throw toAdminError(error);
  }
}

export async function getAdminWaitlist(params: {
  page: number;
  pageSize: number;
  search?: string;
}): Promise<AdminWaitlistResponse> {
  try {
    const response = await adminApi.get<AdminWaitlistResponse>("/admin/waitlist", { params });
    return response.data;
  } catch (error) {
    throw toAdminError(error);
  }
}

export async function downloadAdminWaitlistExport(format: "csv" | "xlsx"): Promise<void> {
  try {
    const response = await adminApi.get<Blob>("/admin/waitlist/export", {
      params: { format },
      responseType: "blob"
    });
    const date = new Date().toISOString().slice(0, 10);
    const type =
      format === "xlsx"
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "text/csv;charset=utf-8";
    downloadBlob(`premium-tier-waitlist-${date}.${format}`, response.data, type);
  } catch (error) {
    throw toAdminError(error);
  }
}

export async function getAdminSettings(): Promise<AdminSettings> {
  try {
    const response = await adminApi.get<AdminSettings>("/admin/settings");
    return response.data;
  } catch (error) {
    throw toAdminError(error);
  }
}
